import { readonly, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useToastStore, type ToastKind } from '@/stores/toast'

const permission = ref<NotificationPermission>(
  'Notification' in window ? Notification.permission : 'denied',
)

const notificationsEnabled = useLocalStorage('notifications_enabled', true)
const backgroundOnly = useLocalStorage('notifications_bg_only', true)
const verbose = useLocalStorage('notifications_verbose', false)

async function requestPermission() {
  if (!('Notification' in window)) return
  if (permission.value !== 'default') return
  permission.value = await Notification.requestPermission()
  // If granted and the user had previously disabled (only possible if they had denied before
  // and then re-allowed via browser settings), re-enable as a convenience.
  if (permission.value === 'granted') notificationsEnabled.value = true
}

function notify(title: string, options?: NotificationOptions & { autoClose?: number }) {
  if (!notificationsEnabled.value) return
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (backgroundOnly.value && document.visibilityState === 'visible') return
  const { autoClose = 5000, ...rest } = options ?? {}
  const n = new Notification(title, { icon: '/favicon.ico', ...rest })
  if (autoClose > 0) setTimeout(() => n.close(), autoClose)
}

interface NotifyEventOptions {
  body?: string
  requireInteraction?: boolean
  autoClose?: number
  verboseOnly?: boolean // toast always shows; notification gated behind verbose setting
  noToast?: boolean // skip toast (notification only)
}

function notifyEvent(
  text: string,
  kind: ToastKind = 'info',
  {
    body,
    requireInteraction,
    autoClose,
    verboseOnly = false,
    noToast = false,
  }: NotifyEventOptions = {},
) {
  if (!noToast) {
    useToastStore().add(text, kind, body ?? null)
  }
  if (!verboseOnly || verbose.value) {
    notify(text, { body, requireInteraction, autoClose })
  }
}

export function useNotifications() {
  return {
    permission: readonly(permission),
    notificationsEnabled,
    backgroundOnly,
    verbose,
    requestPermission,
    notify,
    notifyEvent,
  }
}
